/**
 * AssessFlowContext — carries the new-assessment wizard's choices across screens
 * (Phase 3, mock). Modelled on the verbal-preview usePreview reducer but scoped
 * to the assess flow only and following the repo's React-Context convention
 * (cf. contexts/auth.context.tsx).
 *
 * Screens that participate:
 *   /assess/new        → sets population + chosen scale
 *   /assess/take/:id   → records answers + computed score on submit
 *   /assess/conversation → reads the chosen scale for context
 *   /assess/report     → reads the computed score for banding
 *
 * FRONTEND-ONLY — state lives in memory and resets on reload.
 * TODO[VER-108/Phase3]: replace mock flow state + persist via API (a real
 *   assessment run id would carry these choices server-side).
 */
import React, { createContext, useCallback, useContext, useMemo, useReducer } from "react";

export type AssessPopulation = "1-2-1" | "group";

/** Result captured when a survey is scored, carried to the report screen. */
export type AssessFlowScore = {
    scaleId: string;
    scaleName: string;
    rawScore: number;
    finalScore: number;
    rawRange: [number, number];
    multiplier?: number;
    /** Whether a higher score means higher wellbeing (WHO-5: true; UCLA-20: false). */
    higherIsBetter: boolean;
    answeredOf: { answered: number; total: number };
    cutoffLabel: string | null;
    capturedAt: string;
};

/**
 * Who the assessment is being recorded for (VER-114). The capture endpoint verifies
 * every one of these against the caller's organisation and rejects a participant who
 * is not enrolled on the programme, so these must be real ids — never placeholders.
 */
export type AssessFlowSubject = {
    participantId: number;
    programmeId: number;
    /** Optional: when supplied the endpoint requires it to belong to `programmeId`. */
    sessionId?: number;
};

/** The persisted template being administered (VER-114) — DB ids, not catalog strings. */
export type AssessFlowTemplate = {
    id: number;
    /** Snapshot the answers bind to; the endpoint 409s if it belongs to another template. */
    versionId?: number;
};

export type AssessFlowState = {
    population?: AssessPopulation;
    /** Group only — true when participants have their own devices. */
    devicesAvailable: boolean;
    /** Catalog id of the chosen scale (route param for /assess/take/:scaleId). */
    scaleId?: string;
    subject?: AssessFlowSubject;
    template?: AssessFlowTemplate;
    score?: AssessFlowScore;
    /** Set once the response is persisted — the report reads the server's row, not local state. */
    persistedResponseId?: number;
};

type AssessFlowAction =
    | { type: "CHOOSE_POPULATION"; population: AssessPopulation }
    | { type: "SET_DEVICES_AVAILABLE"; devicesAvailable: boolean }
    | { type: "CHOOSE_SCALE"; scaleId: string }
    | { type: "CHOOSE_SUBJECT"; subject: AssessFlowSubject }
    | { type: "CHOOSE_TEMPLATE"; template: AssessFlowTemplate }
    | { type: "RECORD_SCORE"; score: AssessFlowScore }
    | { type: "RECORD_PERSISTED"; persistedResponseId: number }
    | { type: "RESET_FLOW" };

const INITIAL_STATE: AssessFlowState = {
    devicesAvailable: true,
};

const reducer = (state: AssessFlowState, action: AssessFlowAction): AssessFlowState => {
    switch (action.type) {
        case "CHOOSE_POPULATION":
            return { ...state, population: action.population };
        case "SET_DEVICES_AVAILABLE":
            return { ...state, devicesAvailable: action.devicesAvailable };
        case "CHOOSE_SCALE":
            return { ...state, scaleId: action.scaleId };
        case "CHOOSE_SUBJECT":
            return { ...state, subject: action.subject };
        case "CHOOSE_TEMPLATE":
            return { ...state, template: action.template };
        case "RECORD_SCORE":
            // A re-score supersedes any previously persisted row — clear the id so the
            // report cannot show a stale server response next to a newer local score.
            return { ...state, score: action.score, scaleId: action.score.scaleId, persistedResponseId: undefined };
        case "RECORD_PERSISTED":
            return { ...state, persistedResponseId: action.persistedResponseId };
        case "RESET_FLOW":
            return INITIAL_STATE;
        default:
            return state;
    }
};

type AssessFlowContextValue = {
    state: AssessFlowState;
    choosePopulation: (population: AssessPopulation) => void;
    setDevicesAvailable: (devicesAvailable: boolean) => void;
    chooseScale: (scaleId: string) => void;
    chooseSubject: (subject: AssessFlowSubject) => void;
    chooseTemplate: (template: AssessFlowTemplate) => void;
    recordScore: (score: AssessFlowScore) => void;
    recordPersisted: (persistedResponseId: number) => void;
    resetFlow: () => void;
};

const AssessFlowContext = createContext<AssessFlowContextValue | undefined>(undefined);

export const AssessFlowProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [state, dispatch] = useReducer(reducer, INITIAL_STATE);

    const choosePopulation = useCallback(
        (population: AssessPopulation) => dispatch({ type: "CHOOSE_POPULATION", population }),
        [],
    );
    const setDevicesAvailable = useCallback(
        (devicesAvailable: boolean) => dispatch({ type: "SET_DEVICES_AVAILABLE", devicesAvailable }),
        [],
    );
    const chooseScale = useCallback((scaleId: string) => dispatch({ type: "CHOOSE_SCALE", scaleId }), []);
    const chooseSubject = useCallback(
        (subject: AssessFlowSubject) => dispatch({ type: "CHOOSE_SUBJECT", subject }),
        [],
    );
    const chooseTemplate = useCallback(
        (template: AssessFlowTemplate) => dispatch({ type: "CHOOSE_TEMPLATE", template }),
        [],
    );
    const recordScore = useCallback((score: AssessFlowScore) => dispatch({ type: "RECORD_SCORE", score }), []);
    const recordPersisted = useCallback(
        (persistedResponseId: number) => dispatch({ type: "RECORD_PERSISTED", persistedResponseId }),
        [],
    );
    const resetFlow = useCallback(() => dispatch({ type: "RESET_FLOW" }), []);

    const value = useMemo(
        () => ({
            state,
            choosePopulation,
            setDevicesAvailable,
            chooseScale,
            chooseSubject,
            chooseTemplate,
            recordScore,
            recordPersisted,
            resetFlow,
        }),
        [
            state,
            choosePopulation,
            setDevicesAvailable,
            chooseScale,
            chooseSubject,
            chooseTemplate,
            recordScore,
            recordPersisted,
            resetFlow,
        ],
    );

    return <AssessFlowContext.Provider value={value}>{children}</AssessFlowContext.Provider>;
};

/** Access the assess-flow state. Throws if used outside AssessFlowProvider. */
export const useAssessFlow = (): AssessFlowContextValue => {
    const ctx = useContext(AssessFlowContext);
    if (ctx === undefined) {
        throw new Error("useAssessFlow must be used within an AssessFlowProvider");
    }
    return ctx;
};
